import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { RevealImage } from "@/components/RevealImage";
import Testimonial from "@/components/Testimonial";

/* ────────────────────────────────────────────────────────────
   RECEIVE — Shiva Murti Sacred Healing Arts
   Single accent on this page: VERMILION (booking CTA).
   ──────────────────────────────────────────────────────────── */

const WHO_IT_IS_FOR = [
  "Those moving through or recovering from medical treatment",
  "Athletes, dancers, stunt performers",
  "Full-time mothers and fathers",
  "Those carrying long-held injury, tension, or fatigue",
];

const RECEIVE_TESTIMONIALS = [
  {
    name: "Amy T.",
    quote:
      "Loved the session with Johnny. I had a kidney that hurt so bad, and I didn't like the idea of having surgery, so someone introduced me to Johnny. After the session, that very afternoon my pain went away. It's been a month now, and I am happy and healthy.",
  },
  {
    name: "Alycia G.",
    quote:
      "My experience with Johnny was unexpected. I wasn't sure what I was getting into, but I left feeling more open emotionally and physically. After my first session I left walking like I never had before, or could remember — my movement felt free and no longer tight while in motion. I would highly recommend Johnny to anyone. He is one-of-a-kind.",
  },
  {
    name: "Chikako",
    quote:
      "First time doing Johnny's healing session was amazing. I was dealing with coughing; no remedies were working for me, and I heard about Johnny from a friend. He creates such a comfortable space during the session on Zoom, and I was able to trust him to support me heal right away. I felt energy moving out of my body from my throat, nose and head, and was able to breathe freely by the end of the session. Healing kept happening even after the session, and two days later I felt a sense of peace and calmness in my breathing. It was such a powerful session.",
  },
  {
    name: "Nadine A.",
    quote:
      "My experience with Johnny was truly life-changing. He helped with things even years of clinical therapy could not help with. If you are looking for something that really changes your life for the better, seeing Johnny is truly the best option.",
  },
  {
    name: "Romeo R.",
    quote:
      "I had an energy healing session with Johnny yesterday. At first I wasn't totally sure what I wanted to focus on, but he asked me how I was feeling in the moment and if I was experiencing any challenges. I felt really safe opening up to him, and I appreciated the way he guided me through breathwork. We were able to release some trapped energy and anxiety, and afterwards I felt such a deep sense of calm and peace. I also love that Johnny practises Balinese energy healing — it's such a beautiful experience. I highly recommend working with him: he is kind, a great listener, and creates a space where you feel safe to be vulnerable.",
  },
  {
    name: "Josh M.",
    quote:
      "I can't recommend Johnny highly enough. I originally came to him with a form-related injury that hadn't responded to six months of rehab. Through his energetic healing technique, I experienced a breakthrough — within days, the pain and tension started to ease in a way no other therapy had achieved.",
  },
];

const Receive = () => {
  return (
    <>
      <SEO path="/receive" />

      {/* ── 1. HERO — editorial split: Temple-Black text band + image ──── */}
      <section
        className="bg-temple text-ivory grain"
        data-testid="receive-hero"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 min-h-[80vh]">
          {/* Text band — Temple Black solid, headline anchored here */}
          <div className="flex items-end md:items-center px-6 md:px-14 lg:px-20 pt-32 md:pt-24 pb-16 md:pb-24 order-2 md:order-1">
            <div className="max-w-xl">
              <p className="eyebrow text-gold" data-testid="hero-eyebrow">Receive</p>
              <h1
                className="mt-5 font-serif font-light text-ivory leading-[0.92] tracking-[0.005em]"
                style={{ fontSize: "clamp(2.4rem, 5.2vw, 4.6rem)" }}
                data-testid="hero-headline"
              >
                Come to be cleansed,<br />
                blessed, restored.
              </h1>
              <p className="mt-7 font-sans font-light text-ivory/85 text-base md:text-lg leading-[1.7] max-w-prose">
                A powerful and effective healing modality.
              </p>
            </div>
          </div>

          {/* Image — full vibrant colour, no scrim, no duotone */}
          <div className="relative overflow-hidden order-1 md:order-2 min-h-[55vh] md:min-h-[80vh] grain">
            <RevealImage
              src="/assets/melukat-blessing.png"
              alt="Melukat — sacred water blessing"
              className="absolute inset-0"
              imgClassName="w-full h-full object-cover"
              loading="eager"
              fetchpriority="high"
            />
          </div>
        </div>
      </section>

      {/* ── 2. WHAT THIS IS ──────────────────────────────────────────── */}
      <section className="bg-ivory" data-testid="receive-what">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="eyebrow">What this is</p>
          <div className="mt-10 font-sans font-light text-temple text-base md:text-lg leading-[1.9] space-y-6 max-w-prose">
            <p>
              Shiva Murti is a healing practice created by Ratu Nabe, the
              Balinese priest at the source of this lineage — built to work,
              not to soothe.
            </p>
            <p>
              It moves the energies that run through and around the body: to
              heal what will not heal on its own, to protect, to restore what
              has fallen out of balance. Because the work happens at the level
              of energy itself, the effects are not gradual. Change can come
              fast — felt during the session, and continuing to move through
              the body for 24 to 48 hours after it ends.
            </p>
            <p>
              It draws on classical tantra, sacred Vedic practice, kundalini,
              and Balinese animism — an unbroken lineage carried into practice,
              not a technique assembled for effect.
            </p>
          </div>
          <div className="mt-12" data-testid="what-cta-wrap">
            <a href="#ways" className="btn-vermilion" data-testid="what-cta">
              Receive a healing
              <span aria-hidden>→</span>
            </a>
          </div>
        </div>
      </section>

      {/* ── 2b. WITNESS PULL-QUOTE — live proof of the "speed" claim ─────── */}
      <section
        className="bg-sand border-t border-temple/10"
        data-testid="receive-witness"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <hr className="hairline mx-auto w-16" />
          <blockquote
            className="mt-10 md:mt-12 font-serif font-light text-temple leading-[1.2] tracking-[0.005em]"
            style={{ fontSize: "clamp(1.4rem, 2.6vw, 2.1rem)" }}
            data-testid="witness-quote"
          >
            <span aria-hidden className="text-gold mr-1">“</span>
            In about five minutes, working with Johnny in person, he helped me
            find and channel it — something real effort on my own hadn&apos;t
            gotten me to.
            <span aria-hidden className="text-gold ml-1">”</span>
          </blockquote>
          <p
            className="mt-8 font-sans font-light text-stone text-[0.72rem] tracking-[0.28em] uppercase"
            data-testid="witness-attribution"
          >
            Kevin Lee · Level 1 graduate
          </p>
        </div>
      </section>

      {/* ── 3. WHY RECEIVE — two columns: caning sari left, text right ── */}
      <section className="bg-ivory border-t border-temple/10" data-testid="receive-why">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
            {/* LEFT — caning sari */}
            <div className="md:col-span-5 flex justify-center md:justify-start" data-testid="why-image">
              <img
                src="/assets/caning-sari.png"
                alt="Caning sari offering — flowers in a sacred bowl"
                className="block w-full max-w-[360px] md:max-w-[440px] h-auto select-none"
                loading="lazy"
                decoding="async"
                style={{ background: "transparent" }}
              />
            </div>

            {/* RIGHT — text */}
            <div className="md:col-span-7">
              <p className="eyebrow">Why receive</p>
              <div className="mt-5 font-sans font-light text-temple text-base md:text-lg leading-[1.9] space-y-6 max-w-prose">
                <p>
                  Disempowering energy accumulates — within us, and on us. How it
                  settles is not ours to choose. It can gather as heaviness in the
                  body, a nervous system that will not rest, emotion with nowhere
                  to go, a sense of being out of balance with yourself.
                </p>
                <p>
                  A session meets what has gathered there and clears it. What
                  returns is often felt as ease — circulation, range of movement,
                  lightness, the quiet after something has been set down.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. WHO IT IS FOR — Warm Sand block (tightened) ───────────── */}
      <section className="bg-sand" data-testid="receive-who">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14">
            <div className="md:col-span-4">
              <p className="eyebrow eyebrow--stone">Who it is for</p>
              <h2 className="mt-6 font-serif font-light text-temple text-2xl md:text-4xl leading-[1.15]">
                Wherever the<br />body carries strain.
              </h2>
            </div>
            <ul className="md:col-span-8 list-none" data-testid="who-list">
              {WHO_IT_IS_FOR.map((line, i) => (
                <li
                  key={line}
                  className="border-t border-temple/30 py-5 md:py-6"
                  data-testid={`who-${i}`}
                >
                  <p className="font-serif font-light text-temple text-lg md:text-xl leading-[1.35]">
                    {line}
                  </p>
                </li>
              ))}
              <li className="border-t border-b border-temple/30" />
            </ul>
          </div>
        </div>
      </section>

      {/* ── 5. WAYS TO RECEIVE — In person / Remote (simplified) ─────── */}
      <section id="ways" className="bg-ivory" data-testid="receive-ways">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="eyebrow text-center">Ways to receive</p>
          <p className="mt-10 font-sans font-light text-stone text-base md:text-lg leading-[1.8] max-w-prose mx-auto text-center">
            Two ways to receive the work — in person, where the full ceremony
            is possible, or remotely, wherever you are.
          </p>

          <div className="mt-20 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-[1fr_1px_1fr] gap-0 items-stretch">
            {/* IN PERSON — email intake */}
            <div
              className="md:pr-12 pb-12 md:pb-0 flex flex-col text-center md:text-left"
              data-testid="ways-in-person"
            >
              <h3 className="font-serif font-light text-temple text-3xl md:text-4xl leading-[1.1]">
                In person
              </h3>
              <p className="mt-6 font-sans font-light text-temple text-base leading-[1.8] max-w-prose">
                Offered one to one — the complete practice, including flower
                ceremony and water blessing where called for.
              </p>
              <div className="mt-auto pt-10">
                <a
                  href="mailto:info@shivamurtihealingarts.com?subject=In%20person%20session%20inquiry"
                  className="btn-vermilion-ghost"
                  data-testid="ways-in-person-cta"
                >
                  Send email
                  <span aria-hidden>→</span>
                </a>
              </div>
            </div>

            {/* Sacred-Gold vertical seam */}
            <div aria-hidden className="hidden md:block w-px bg-gold/60 self-stretch" />
            <div aria-hidden className="md:hidden h-px w-full bg-gold/60" />

            {/* REMOTE — direct calendar booking */}
            <div
              className="md:pl-12 pt-12 md:pt-0 flex flex-col text-center md:text-left"
              data-testid="ways-remote"
            >
              <h3 className="font-serif font-light text-temple text-3xl md:text-4xl leading-[1.1]">
                Remote
              </h3>
              <p className="mt-6 font-sans font-light text-temple text-base leading-[1.8] max-w-prose">
                A healing session reaching you wherever you are — held via
                Zoom or, when needed, by photograph.
              </p>
              <div className="mt-auto pt-10">
                <a
                  href="https://api.leadconnectorhq.com/widget/booking/Drejxcq2efFbcdofRM2y"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-vermilion-ghost"
                  data-testid="ways-remote-cta"
                >
                  Healing session
                  <span aria-hidden>→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6. HEALING SOCIALS ──────────────────────────────────────── */}
      <section className="bg-ivory border-t border-temple/10" data-testid="receive-socials">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <p className="eyebrow">Healing socials</p>
          <div className="mt-10 font-sans font-light text-temple text-base md:text-lg leading-[1.9] space-y-6 max-w-prose">
            <p>
              A tradition carried from Ratu Nabe&apos;s own practice:
              complimentary healings offered to the community by students of
              the lineage. It is how we give back — to those with less,
              wherever the lineage travels — and, for many, it is the first
              doorway into Shiva Murti.
            </p>
            <p>
              If you are new to the work, or already receiving and want added
              support, a healing social is a place to begin.
            </p>
            <p className="pt-6 border-t border-temple/15 text-stone">
              A healing social is also the rite a student hosts to complete
              the path — week seven of seven.
            </p>
          </div>

          {/* Next gathering + CTA */}
          <div
            className="mt-14 pt-10 border-t border-gold/60 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
            data-testid="socials-next"
          >
            <div>
              <span
                className="block font-sans text-[0.68rem] tracking-[0.28em] uppercase"
                style={{ color: "var(--gold)" }}
                data-testid="socials-next-eyebrow"
              >
                Next gathering
              </span>
              <span
                className="mt-3 block font-serif font-light text-temple leading-[1.15] text-2xl md:text-3xl"
                data-testid="socials-next-date"
              >
                Sunday, August 9th, 2026
              </span>
              <span
                className="mt-2 block font-sans text-[0.72rem] tracking-[0.24em] uppercase text-stone"
                data-testid="socials-next-time"
              >
                4:30 – 6:00 pm PST · Remote
              </span>
              <span
                className="mt-3 block font-sans text-[0.72rem] tracking-[0.28em] uppercase"
                style={{ color: "var(--vermilion)" }}
                data-testid="socials-next-price"
              >
                Complimentary · No charge
              </span>
            </div>
            <a
              href="https://api.leadconnectorhq.com/widget/form/msVRkRB6Pzh1jV4DkkVG"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-vermilion-ghost self-start md:self-end whitespace-nowrap"
              data-testid="socials-cta"
            >
              Reserve a place
              <span aria-hidden>→</span>
            </a>
          </div>
          <p
            className="mt-6 font-sans font-light text-stone text-sm leading-[1.8] max-w-prose"
            data-testid="socials-note"
          >
            Space is held quietly — request a place and details for joining will follow.
          </p>
        </div>
      </section>

      {/* ── 7. THE PRACTITIONER ─────────────────────────────────────── */}
      <section className="bg-ivory border-t border-temple/10" data-testid="receive-practitioner">
        <div className="max-w-[1320px] mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
            <div className="md:col-span-6">
              <RevealImage
                src="/assets/practitioner.png"
                alt="The keeper of the lineage"
                className="w-full"
                style={{ aspectRatio: "1 / 1" }}
                imgClassName="w-full h-full object-cover"
                testId="practitioner-portrait"
              />
            </div>
            <div className="md:col-span-6 md:pt-10">
              <p className="eyebrow">The practitioner</p>
              <h2 className="mt-6 font-serif font-light text-temple text-3xl md:text-5xl leading-[1.1]">
                Johnny Cantiveros.
              </h2>
              <div className="mt-8 font-sans font-light text-temple text-base md:text-lg leading-[1.9] space-y-6 max-w-prose">
                <p>
                  Johnny Cantiveros is a teacher within the Shiva Murti Bali
                  lineage. He came to this work through Ratu Nabe, the source
                  of the lineage — and has studied under him for 4 years in
                  Bali and through his teacher Jero Jason for 6 years.
                </p>
                <p>
                  What he carries was not learned from books. It was
                  transmitted: through ceremony, through sacred water, through
                  initiation, in the old way, from teacher to student. After
                  having a vision to heal with his hands, this is the path that
                  was presented to him.
                </p>
                <p>
                  He holds sessions in person and few at a time. The work is
                  not hurried, and it is not for everyone — only for those
                  ready to be received.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 7b. TESTIMONIALS — 2-column poleng grid (Ivory ⇄ Temple Black) ── */}
      <section data-testid="receive-testimonials">
        <div className="bg-ivory border-t border-temple/10" data-testid="testimonials-intro">
          <div className="max-w-3xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-6 md:pb-8">
            <p className="eyebrow text-center">In their own words</p>
          </div>
        </div>

        {/* Poleng grid: 2 columns on md+, checkerboard alternation
            (Ivory ⇄ Temple Black) creates the sacred duality pattern.
            Formula (i + floor(i/2)) % 2 gives the true 2D checker pattern:
            [Ivory][Temple]
            [Temple][Ivory]
            [Ivory][Temple] ... */}
        <div
          className="grid grid-cols-1 md:grid-cols-2"
          data-testid="testimonials-grid"
        >
          {RECEIVE_TESTIMONIALS.map((t, i) => {
            const isDark = (i + Math.floor(i / 2)) % 2 === 1;
            const bg = isDark ? "bg-temple" : "bg-ivory";
            const quoteColor = isDark ? "text-ivory" : "text-temple";
            const nameColor = isDark ? "text-ivory/60" : "text-stone";
            return (
              <div
                key={t.name}
                className={`${bg} ${isDark ? "grain" : ""} flex`}
                data-testid={`testimonial-band-${i}`}
              >
                <figure
                  className="w-full max-w-xl mx-auto px-8 md:px-10 py-14 md:py-20 flex flex-col justify-center"
                  data-testid={`receive-testimonial-${i}`}
                >
                  <hr className="hairline mx-auto w-14" />
                  <blockquote
                    className={`mt-8 md:mt-10 font-serif font-light leading-[1.35] tracking-[0.005em] text-center ${quoteColor}`}
                    style={{ fontSize: "clamp(1.1rem, 1.5vw, 1.35rem)" }}
                  >
                    <span aria-hidden className="text-gold mr-1">“</span>
                    {t.quote}
                    <span aria-hidden className="text-gold ml-1">”</span>
                  </blockquote>
                  <figcaption
                    className={`mt-8 font-sans font-light text-[0.7rem] tracking-[0.28em] uppercase text-center ${nameColor}`}
                  >
                    {t.name}
                  </figcaption>
                </figure>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default Receive;
